import { Box, Typography, ButtonBase, alpha, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
// Heritage theme colors embedded

// Playlist color palette (lighter versions for better visibility)
const PLAYLIST_COLORS = [
    '#a8c5e0', // navy light
    '#7fb8b8', // teal light
    '#7fb89f', // emerald light
    '#e6c84d', // goldLight bright
    '#d4a5d4', // סגול בהיר
    '#f5b57a', // כתום בהיר
    '#6dd4b8', // ירוק ים בהיר
    '#f08080', // אדום בהיר
    '#87ceeb', // כחול בהיר
    '#dda0dd', // סגול כהה בהיר
];

// Get playlist color
const getPlaylistColor = (playlistId, allPlaylists) => {
    const playlist = allPlaylists.find(p => p.playlistId === playlistId);
    if (playlist?.color) return playlist.color;

    const index = allPlaylists.findIndex(p => p.playlistId === playlistId);
    return index === -1 ? PLAYLIST_COLORS[0] : PLAYLIST_COLORS[index % PLAYLIST_COLORS.length];
};

/**
 * PlaylistToolbar - Clean minimal playlist selector
 */
const PlaylistToolbar = ({
    playlists,
    activePlaylist,
    onSelectPlaylist
}) => {
    return (
        <Box sx={{ mb: 3 }}>
            {/* Header */}
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 600,
                    color: '#64748b',
                    fontSize: '0.875rem',
                    mb: 1,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                }}
            >
                בחר מערך תצוגה
            </Typography>

            {/* Playlists */}
            {playlists.length === 0 ? (
                <Box
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px dashed #e2e8f0'
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        אין מערכי תצוגה זמינים
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        alignItems: 'center'
                    }}
                >
                    {playlists.map((playlist) => {
                        const color = getPlaylistColor(playlist.playlistId, playlists);
                        const isActive = activePlaylist?.playlistId === playlist.playlistId;
                        const isPublished = playlist.isPublished !== false; // ברירת מחדל true אם לא צוין

                        const button = (
                            <ButtonBase
                                key={playlist.playlistId}
                                onClick={() => isPublished && onSelectPlaylist(playlist)}
                                disabled={!isPublished}
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: '8px',
                                    bgcolor: !isPublished
                                        ? '#f8fafc'
                                        : isActive
                                            ? alpha(color, 0.12)
                                            : '#ffffff',
                                    border: !isPublished
                                        ? '1px dashed #cbd5e1'
                                        : isActive
                                            ? `2px solid ${color}`
                                            : '1px solid #e2e8f0',
                                    opacity: !isPublished ? 0.6 : 1,
                                    cursor: !isPublished ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': isPublished ? {
                                        bgcolor: isActive ? alpha(color, 0.15) : '#f8fafc',
                                        borderColor: isActive ? color : '#cbd5e1',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                    } : {},
                                    '&:active': isPublished ? {
                                        transform: 'translateY(0)'
                                    } : {}
                                }}
                            >
                                {/* Color Dot */}
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        bgcolor: color,
                                        flexShrink: 0,
                                        opacity: !isPublished ? 0.5 : 1
                                    }}
                                />

                                {/* Playlist Name */}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: isActive ? 600 : 500,
                                        color: !isPublished
                                            ? '#94a3b8'
                                            : isActive
                                                ? '#1e293b'
                                                : '#475569',
                                        fontSize: '0.9rem',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {playlist.name}
                                </Typography>

                                {/* Active Check */}
                                {isActive && isPublished && (
                                    <CheckIcon
                                        sx={{
                                            fontSize: 16,
                                            color: color,
                                            ml: 0.5
                                        }}
                                    />
                                )}
                            </ButtonBase>
                        );

                        // Wrap with Tooltip if unpublished
                        if (!isPublished) {
                            return (
                                <Tooltip
                                    key={playlist.playlistId}
                                    title="לא ניתן לבחור - עדיין לא פורסמה גרסה"
                                    arrow
                                    placement="top"
                                >
                                    <span>{button}</span>
                                </Tooltip>
                            );
                        }

                        return button;
                    })}
                </Box>
            )}
        </Box>
    );
};

export default PlaylistToolbar;
